import {
    authTokenSignal,
    requestAuthToken,
    waitForAuthToken,
} from "@/client/core/services/auth-token.signal";
import { kServerUrl } from "@/client/utils/serverUrl";
import { sha256HexOfFile } from "@/client/utils/sha256";
import { isApiErrorBody } from "@/shared/api-endpoint";
import {
    isUploadUrlsSuccessResponse,
    kUploadUrlsRoute,
    type UploadUrlsRequestBody,
} from "@/shared/upload-url.api";

const kInitialTokenWaitMs = 3000;
const kTokenRefreshWaitMs = 5000;

const buildUploadUrlHeaders = (): HeadersInit => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const token = authTokenSignal.value.accessToken;
    if (token !== undefined) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
};

const getContentType = (file: File): string =>
    file.type !== "" ? file.type : "application/octet-stream";

export const uploadFilesToS3 = async <K extends string>(
    files: Record<K, File>,
): Promise<Record<K, string> | Error> => {
    const entries = Object.entries<File>(files);

    // Hash every file in parallel before asking the server for upload URLs.
    // The server uses the hash as the S3 key (content-addressed), so identical
    // bytes map to the same key across requests / users / sessions and the PUT
    // is skipped entirely on cache hits.
    let hashes: Record<string, string>;
    try {
        const hashEntries = await Promise.all(
            entries.map(async ([name, file]) => [name, await sha256HexOfFile(file)] as const),
        );
        hashes = Object.fromEntries(hashEntries);
    } catch (error) {
        return new Error(
            error instanceof Error
                ? `Failed to hash file: ${error.message}`
                : "Failed to hash file",
        );
    }

    const requestBody: UploadUrlsRequestBody = {
        files: {},
    };
    for (const [name, file] of entries) {
        requestBody.files[name] = {
            contentType: getContentType(file),
            contentLength: file.size,
            contentHash: hashes[name],
        };
    }

    const url = `${kServerUrl}${kUploadUrlsRoute}`;
    const serializedBody = JSON.stringify(requestBody);
    await waitForAuthToken(kInitialTokenWaitMs);
    let uploadUrlResponse = await fetch(url, {
        method: "POST",
        headers: buildUploadUrlHeaders(),
        body: serializedBody,
    });

    if (uploadUrlResponse.status === 401) {
        await requestAuthToken(kTokenRefreshWaitMs);
        uploadUrlResponse = await fetch(url, {
            method: "POST",
            headers: buildUploadUrlHeaders(),
            body: serializedBody,
        });
    }

    if (!uploadUrlResponse.ok) {
        try {
            const json: unknown = await uploadUrlResponse.json();
            if (isApiErrorBody(json)) {
                return new Error(json.error);
            }
        } catch {
            // Response body was not valid JSON — fall through to generic error
        }
        return new Error(`Failed to get upload URLs: ${String(uploadUrlResponse.status)}`);
    }

    const json: unknown = await uploadUrlResponse.json();
    if (!isUploadUrlsSuccessResponse(json)) {
        return new Error("Invalid upload URL response from server");
    }

    const result = {} as Record<K, string>;
    for (const [name, upload] of Object.entries(json.uploads)) {
        if (!Object.hasOwn(files, name)) {
            return new Error(`Server returned unexpected upload key "${name}"`);
        }
        const file = files[name as K];

        // Server omits `uploadUrl` when the object already exists at this
        // content-addressed key. Skip the PUT in that case.
        if (upload.uploadUrl === undefined) {
            result[name as K] = upload.key;
            continue;
        }

        const putResponse = await fetch(upload.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": getContentType(file) },
            body: file,
        });
        if (!putResponse.ok) {
            return new Error(`S3 upload failed for "${name}": ${String(putResponse.status)}`);
        }
        result[name as K] = upload.key;
    }

    return result;
};
